import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Buton, Etiket, Girdi, toast } from '@emanetx/ui';
import type { DolapBoyutu, FiyatKurali } from '@emanetx/sdk';
import { useNoktaListesi } from '../noktalar/api.js';
import { useFiyatKuralGuncelle, useFiyatKuralOlustur } from './api.js';

const GUNLER: { ad: string; bit: number }[] = [
  { ad: 'Pzt', bit: 1 },
  { ad: 'Sal', bit: 2 },
  { ad: 'Çar', bit: 4 },
  { ad: 'Per', bit: 8 },
  { ad: 'Cum', bit: 16 },
  { ad: 'Cmt', bit: 32 },
  { ad: 'Paz', bit: 64 },
];

interface Ozellikler {
  acik: boolean;
  onDegisim: (a: boolean) => void;
  kural?: FiyatKurali | null;
}

export function FiyatKuralDialog({ acik, onDegisim, kural }: Ozellikler) {
  const duzenleme = !!kural;
  const noktalar = useNoktaListesi({ sayfa: 1, boyut: 100 });
  const olustur = useFiyatKuralOlustur();
  const guncelle = useFiyatKuralGuncelle();

  const [noktaId, setNoktaId] = useState('');
  const [boyut, setBoyut] = useState<DolapBoyutu | ''>('');
  const [oncelik, setOncelik] = useState(100);
  const [gunlerMask, setGunlerMask] = useState(127);
  const [saatBaslangic, setSaatBaslangic] = useState(0);
  const [saatBitis, setSaatBitis] = useState(24);
  const [carpan, setCarpan] = useState('1.000');
  const [sabitMod, setSabitMod] = useState(false);
  const [sabitTutar, setSabitTutar] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [aktif, setAktif] = useState(true);

  useEffect(() => {
    if (!acik) return;
    if (kural) {
      setNoktaId(kural.noktaId ?? '');
      setBoyut(kural.dolapBoyutu ?? '');
      setOncelik(kural.oncelik);
      setGunlerMask(kural.gunlerMask);
      setSaatBaslangic(kural.saatBaslangic);
      setSaatBitis(kural.saatBitis);
      setCarpan(kural.carpan);
      setSabitMod(!!kural.sabitTutar);
      setSabitTutar(kural.sabitTutar ?? '');
      setAciklama(kural.aciklama);
      setAktif(kural.aktif);
    } else {
      setNoktaId('');
      setBoyut('');
      setOncelik(100);
      setGunlerMask(127);
      setSaatBaslangic(0);
      setSaatBitis(24);
      setCarpan('1.000');
      setSabitMod(false);
      setSabitTutar('');
      setAciklama('');
      setAktif(true);
    }
  }, [acik, kural]);

  const calisiyor = olustur.isPending || guncelle.isPending;
  const formGecerli =
    aciklama.trim().length >= 3 &&
    saatBitis > saatBaslangic &&
    /^\d+(\.\d{1,3})?$/.test(carpan) &&
    (!sabitMod || /^\d+(\.\d{1,2})?$/.test(sabitTutar));

  const govdeOlustur = () => ({
    noktaId: noktaId || null,
    dolapBoyutu: (boyut || null) as DolapBoyutu | null,
    oncelik,
    gunlerMask,
    saatBaslangic,
    saatBitis,
    carpan,
    sabitTutar: sabitMod ? sabitTutar : null,
    aciklama: aciklama.trim(),
    aktif,
  });

  const gonder = async () => {
    try {
      if (duzenleme && kural) {
        await guncelle.mutateAsync({ id: kural.id, ...govdeOlustur() });
        toast.success('Kural güncellendi');
      } else {
        await olustur.mutateAsync(govdeOlustur());
        toast.success('Kural oluşturuldu');
      }
      onDegisim(false);
    } catch (err) {
      const m =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'İşlem başarısız';
      toast.error(m);
    }
  };

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">
              {duzenleme ? 'Fiyat Kuralını Düzenle' : 'Yeni Fiyat Kuralı'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                aria-label="Kapat"
                className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (formGecerli && !calisiyor) void gonder();
            }}
            className="space-y-4"
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Etiket htmlFor="nokta">Nokta (boş → tüm noktalar)</Etiket>
                <select
                  id="nokta"
                  value={noktaId}
                  onChange={(e) => setNoktaId(e.target.value)}
                  className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm"
                >
                  <option value="">Tümü</option>
                  {noktalar.data?.veriler.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.kod} — {n.ad}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Etiket htmlFor="boyut">Dolap Boyutu (boş → tüm boyutlar)</Etiket>
                <select
                  id="boyut"
                  value={boyut}
                  onChange={(e) => setBoyut(e.target.value as DolapBoyutu | '')}
                  className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 mt-1 h-11 w-full rounded-lg border bg-white px-3 text-sm"
                >
                  <option value="">Tümü</option>
                  <option value="KUCUK">Küçük (S)</option>
                  <option value="ORTA">Orta (M)</option>
                  <option value="BUYUK">Büyük (L)</option>
                  <option value="EKSTRA">Ekstra (XL)</option>
                </select>
              </div>
            </div>

            <div>
              <Etiket>Geçerli Günler</Etiket>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {GUNLER.map((g) => {
                  const secili = (gunlerMask & g.bit) !== 0;
                  return (
                    <button
                      key={g.bit}
                      type="button"
                      onClick={() => setGunlerMask((m) => (secili ? m & ~g.bit : m | g.bit))}
                      className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                        secili
                          ? 'border-marka-500 bg-marka-500 text-fume-900'
                          : 'border-fume-200 text-fume-600 hover:bg-fume-50 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-300 bg-white'
                      }`}
                    >
                      {g.ad}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Etiket htmlFor="oncelik">Öncelik (düşük = öncelikli)</Etiket>
                <Girdi
                  id="oncelik"
                  type="number"
                  min={0}
                  max={1000}
                  value={oncelik}
                  onChange={(e) => setOncelik(Number(e.target.value))}
                />
              </div>
              <div>
                <Etiket htmlFor="saatBas">Saat başlangıç</Etiket>
                <Girdi
                  id="saatBas"
                  type="number"
                  min={0}
                  max={24}
                  value={saatBaslangic}
                  onChange={(e) => setSaatBaslangic(Number(e.target.value))}
                />
              </div>
              <div>
                <Etiket htmlFor="saatBit">Saat bitiş</Etiket>
                <Girdi
                  id="saatBit"
                  type="number"
                  min={0}
                  max={24}
                  value={saatBitis}
                  onChange={(e) => setSaatBitis(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="flex items-center justify-between">
                  <Etiket>Mod</Etiket>
                  <label className="text-fume-500 flex cursor-pointer items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={sabitMod}
                      onChange={(e) => setSabitMod(e.target.checked)}
                    />
                    Sabit tutar kullan
                  </label>
                </div>
                {sabitMod ? (
                  <Girdi
                    type="text"
                    inputMode="decimal"
                    value={sabitTutar}
                    onChange={(e) => setSabitTutar(e.target.value)}
                    placeholder="50.00"
                    className="mt-1"
                  />
                ) : (
                  <Girdi
                    type="text"
                    inputMode="decimal"
                    value={carpan}
                    onChange={(e) => setCarpan(e.target.value)}
                    placeholder="1.000"
                    className="mt-1"
                  />
                )}
                <p className="text-fume-400 mt-1 text-xs">
                  {sabitMod
                    ? 'Saatlik baz fiyatın yerine bu sabit tutar uygulanır.'
                    : 'Baz fiyat bu çarpanla çarpılır (1.5 = +%50).'}
                </p>
              </div>
              <div>
                <Etiket htmlFor="aktif">Durum</Etiket>
                <label className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 mt-1 flex h-11 cursor-pointer items-center gap-2 rounded-lg border bg-white px-3 text-sm">
                  <input
                    id="aktif"
                    type="checkbox"
                    checked={aktif}
                    onChange={(e) => setAktif(e.target.checked)}
                  />
                  Aktif
                </label>
              </div>
            </div>

            <div>
              <Etiket htmlFor="aciklama">
                Açıklama <span className="text-red-500">*</span>
              </Etiket>
              <Girdi
                id="aciklama"
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
                placeholder="Örn. Hafta sonu peak +%50"
                required
                minLength={3}
                maxLength={200}
              />
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" boyut="orta" disabled={calisiyor}>
                  İptal
                </Buton>
              </Dialog.Close>
              <Buton
                stil="birincil"
                boyut="orta"
                type="submit"
                disabled={!formGecerli || calisiyor}
              >
                {calisiyor ? 'Kaydediliyor...' : duzenleme ? 'Güncelle' : 'Oluştur'}
              </Buton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function gunlerOzeti(mask: number): string {
  if (mask === 127) return 'Her gün';
  if (mask === 31) return 'Hafta içi';
  if (mask === 96) return 'Hafta sonu';
  const seciliBitler = GUNLER.filter((g) => (mask & g.bit) !== 0).map((g) => g.ad);
  return seciliBitler.join(', ') || '—';
}
